import LocaleButton from "@common/components/LocaleButton";
import {
  AccountIcon,
  ArrowLeftIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LoadingIconGlobeIcon,
  LogoutIcon,
} from "@common/components/icons";
import LocalImage from "@common/components/LocalImage";
import { formatCurrency } from "@common/helpers/numberFormatting";
import CoreLayout from "@layouts/core/components/CoreLayout";
import PurchaseStepper from "@modules/purchase/components/PurchaseStepper";
import {
  getAllPurchaseStepsForFlow,
  getPreviousStep,
  getStepById,
  aftermarketDetailsStep,
  PurchaseStep,
  stepIsBeforeSigning,
  purchaseLastStep,
} from "@modules/purchase/types/PurchaseStep";
import RightRideLogo from "@public/images/logo-rightride-autocanada.svg";
import KijijiLogo from "@public/images/logo-kijiji.svg";
import IALogo from "@public/images/logo-ia.svg";
import classNames from "classnames";
import { Trans, useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import { MouseEvent, ReactNode, useEffect, useMemo, useState } from "react";
import PurchaseInformation from "@modules/purchase/components/aftermarket/PurchaseInformation";
import { Purchase, PurchasePreview } from "@modules/purchase/types/Purchase";
import { Transition } from "@headlessui/react";
import PurchaseIntroInformation from "@modules/purchase/components/aftermarket/PurchaseIntroInformation";
import useUser from "@modules/user/hooks/useUser";
import CollapsibleLoginButton from "@components/CollapsibleLoginButton";
import LoginFlowWrapper from "@components/LoginFlowWrapper";
import { loginMode } from "@modules/user/types/LoginModalMode";
import Modal from "@common/components/Modal";
import CoverageOverwriteModal from "@modules/purchase/components/coverage/CoverageOverwriteModal";
import useSetActiveAftermarketPurchaseMutation from "@modules/purchase/hooks/useSetActiveAftermarketPurchaseMutation";
import useAftermarketPurchase from "@modules/purchase/hooks/useAftermarketPurchase";
import { useQueryClient } from "@tanstack/react-query";
import purchaseQueryKeys from "@modules/purchase/purchaseQueryKeys";
import LoadingSpinner from "@common/components/LoadingSpinner";
import PurchaseSourceVehicleImage from "@modules/purchase/components/PurchaseSourceVehicleImage";
import Button from "@components/Button";
import useDevice from "@common/hooks/useDevice";
import { gtmPushData } from "@common/helpers/gtm";
import CoverageDetailsModal from "@modules/purchase/components/aftermarket/CoverageDetailsModal";
import useGetDealLink from "@modules/purchase/hooks/useGetDealLink";
import ConsumerPurchaseLayoutContext, {
  ConsumerPurchaseLayoutContextState,
} from "@modules/purchase/ConsumerPurchaseLayoutContext";
import HamburgerMenu from "@common/components/HamburgerMenu";
import { getAlternateLocaleByCode } from "@common/types/Locale";
import useUpdateUserLanguageMutation from "@modules/user/hooks/useUpdateUserLanguageMutation";
import { TranslationKeys } from "src/@types/i18next";
import CustomerAvatar from "@modules/admin/customers/components/CustomerAvatar";
import useLogoutUserMutation from "@modules/user/hooks/useLogoutUserMutation";
import { applyPromoCode } from "@modules/purchase/hooks/useAftermarketApplyPromoCodeMutation";
import { removeQueryParams } from "@modules/core/routing/routingHelpers";
import PromoCodeAppliedModal from "@modules/purchase/components/aftermarket/PromoCodeAppliedModal";

type Props = {
  children: ReactNode;
  currentStep?: PurchaseStep;
  showBackButton?: boolean;
  purchase?: Purchase;
  purchasePreview?: PurchasePreview;
  removePadding?: boolean;
  onLoginSuccess?: () => void;
  onLoginClose?: () => void;
  isIntroduction?: boolean;
  headerContainer?: ReactNode;
  isOnIndex?: boolean;
  isOnLanding?: boolean;
  autoRedirectToPurchaseAfterLogin?: boolean;
};

type ChildrenContainerProps = {
  children: ReactNode;
  purchase?: Purchase;
  purchasePreview?: PurchasePreview;
  currentStep?: PurchaseStep;
  removePadding?: boolean;
  isOnIndex?: boolean;
};

const ChildrenContainer = ({
  children,
  purchase,
  purchasePreview,
  currentStep,
  removePadding = false,
  isOnIndex = false,
}: ChildrenContainerProps) => {
  if (currentStep?.isPointOfNoReturn) {
    return <div>{children}</div>;
  }

  return (
    <div
      className={`overflow-hidden lg:min-h-[calc(100vh-148px)] xl:min-h-[calc(100vh-92px)] ${
        isOnIndex ? "md:flex" : "flex"
      }`}
    >
      <div
        className={classNames("flex-auto", {
          "px-4 py-8 lg:px-12 xl:px-28 xl:py-12 w-4/6": !removePadding,
        })}
      >
        {children}
      </div>
      {currentStep && (
        <div className="bg-gray-25 w-1/3 hidden lg:block">
          <div className="sticky top-12">
            {purchase && purchase.vehicle && (
              <PurchaseInformation
                purchasePreview={purchasePreview}
                purchase={purchase}
              />
            )}
            {!purchase?.vehicle && <PurchaseIntroInformation />}
          </div>
        </div>
      )}
    </div>
  );
};

const shouldShowReSignModal = (
  previousStep: PurchaseStep,
  currentStep: PurchaseStep,
  anyDocumentIsSigned: boolean
) => {
  return (
    previousStep &&
    currentStep &&
    anyDocumentIsSigned &&
    stepIsBeforeSigning(previousStep) &&
    !stepIsBeforeSigning(currentStep)
  );
};

const getTotalPrice = (
  purchase?: Purchase,
  purchasePreview?: PurchasePreview
) => {
  if (purchasePreview != null) return purchasePreview.pricing.totalPrice;
  if (purchase != null) return purchase.pricing?.totalPrice;
  return 0;
};

const CoveragePurchaseLayout = ({
  children,
  currentStep,
  showBackButton = true,
  purchase,
  purchasePreview,
  removePadding,
  onLoginSuccess,
  onLoginClose,
  isIntroduction = false,
  headerContainer,
  isOnIndex = false,
  isOnLanding = false,
  autoRedirectToPurchaseAfterLogin: autoRedirectAfterLogin = true,
}: Props) => {
  const { user } = useUser();
  const { t } = useTranslation(["purchase", "common"]);
  const router = useRouter();
  const locale = router?.locale || "en";
  const { source, login } = router.query;
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);

  const token = router.query.token as string;
  const { data } = useGetDealLink(token);

  useEffect(() => {
    if (user?.isLoggedIn) return;
    setShowLoginModal(
      (login !== undefined && login === "true") ||
        (data !== undefined && data.type === "login")
    );
  }, [setShowLoginModal, login, user, data]);

  const previousStep =
    showBackButton && currentStep
      ? getPreviousStep("aftermarket", currentStep)
      : undefined;
  const [isOrderSummaryOpen, setIsOrderSummaryOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [showCoverageDetailsModal, setShowCoverageDetailsModal] =
    useState(false);
  const consumerPurchaseLayoutContextState: ConsumerPurchaseLayoutContextState =
    useMemo(
      () => ({
        showCoverageDetailsModalState: [
          showCoverageDetailsModal,
          setShowCoverageDetailsModal,
        ],
      }),
      [showCoverageDetailsModal]
    );
  const device = useDevice();
  const {
    isFetching: isPurchaseFetching,
    isStale: isPurchaseStale,
    refetch: refetchPurchase,
  } = useAftermarketPurchase();
  const setActiveAftermarket = useSetActiveAftermarketPurchaseMutation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isPurchaseFetching || !purchase || !purchase?.sourceInfo) return;

    // Sets the GTM data layer for source params.
    const { appId, sessionId, adId, userId, locationId, ctaId } =
      purchase.sourceInfo;
    gtmPushData({
      source: purchase.dealSource,
      sourceAppId: appId || "",
      sourceSessionId: sessionId || "",
      sourceParamIds: `Ad ID: ${adId || ""}, User ID: ${
        userId || ""
      }, Location ID: ${locationId || ""}, CTA ID: ${ctaId || ""}`,
    });
  }, [isPurchaseFetching, purchase]);

  const { promo } = router.query;
  const [promoCodeName, setPromoCodeName] = useState("");
  const [showPromoCodeApplied, setShowPromoCodeApplied] =
    useState<boolean>(false);

  useEffect(() => {
    if (promo && router.isReady && !isPurchaseFetching) {
      setPromoCodeName(promo as string);
      if (purchase) {
        // Directly calling the applyPromoCode function to ensure it runs synchronously.
        applyPromoCode({
          dealId: purchase.id,
          promoCode: promo as string,
        }).then(
          () => {
            removeQueryParams(router, ["promo"]);
            setShowPromoCodeApplied(true);
          },
          () => {
            removeQueryParams(router, ["promo"]);
          }
        );
      } else {
        setShowPromoCodeApplied(true);
        removeQueryParams(router, ["promo"]);
      }
    }
  }, [isPurchaseFetching, promo, purchase, refetchPurchase, router]);

  useEffect(() => {
    // wait until purchase is not stale
    const { loginSuccessful } = router.query;

    if (
      isPurchaseStale ||
      isPurchaseFetching ||
      (!purchase?.currentStepId &&
        !purchase?.overwriteCandidate?.currentStepId &&
        !loginSuccessful)
    )
      return;

    if (purchase?.currentStepId || purchase?.overwriteCandidate) {
      if (
        purchase?.overwriteCandidate &&
        router.pathname !== purchaseLastStep.aftermarket.href
      ) {
        setShowOverwriteModal(true);
      } else if (
        purchase?.currentStepId &&
        loginSuccessful === "true" &&
        autoRedirectAfterLogin
      ) {
        // does not have an overwrite candidate, but the user just logged in
        const purchaseStep = getStepById("aftermarket", purchase.currentStepId);

        if (purchaseStep && router.pathname !== purchaseStep.href) {
          router.push({
            pathname: purchaseStep.href,
          });
        }
      }
    }

    setIsPageLoading(false);
  }, [
    autoRedirectAfterLogin,
    isPurchaseFetching,
    isPurchaseStale,
    purchase,
    router,
    source,
  ]);

  const onLoginClick = () => {
    setShowLoginModal(true);
  };

  const onLoginModalClose = () => {
    if (onLoginClose) {
      onLoginClose();
    }
    setShowLoginModal(false);
  };

  const onLogin = async () => {
    setIsPageLoading(true);

    queryClient.invalidateQueries({
      queryKey: purchaseQueryKeys.activeAftermarketPurchase(),
    });

    onLoginSuccess?.();
  };

  const redirectToCurrentStep = (currentStepId: number | null) => {
    setShowOverwriteModal(false);

    const purchaseStep = getStepById(
      "aftermarket",
      currentStepId ?? aftermarketDetailsStep.id
    );

    if (purchaseStep) {
      router.push({
        pathname: purchaseStep.href,
      });
    }
  };

  const handleOnResumePurchaseClick = () => {
    if (!purchase) return;

    if (purchase.currentStepId === purchaseLastStep.aftermarket.id) {
      redirectToCurrentStep(purchase.currentStepId);
    } else {
      setActiveAftermarket.mutate(purchase.id, {
        onSuccess: () => redirectToCurrentStep(purchase.currentStepId),
      });
    }
  };

  const handleOnOverwritePurchaseClick = () => {
    if (!purchase?.overwriteCandidate) return;

    setActiveAftermarket.mutate(purchase.overwriteCandidate.id, {
      onSuccess: () =>
        redirectToCurrentStep(purchase.overwriteCandidate.currentStepId),
    });
  };

  const handleBackClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      previousStep &&
      currentStep &&
      purchase &&
      shouldShowReSignModal(
        previousStep,
        currentStep,
        purchase.documentSummary.anyDocumentIsSigned
      )
    ) {
      event.preventDefault();
      setShowModal(true);
    }
  };

  const getLoginButtonText = () => {
    let buttonText:
      | "common:save_progress"
      | "common:login"
      | "common:log_in_and_resume" = "common:save_progress";

    if (isIntroduction || !currentStep) {
      if (device?.isMobile) {
        buttonText = "common:log_in_and_resume";
      } else {
        buttonText = "common:log_in_and_resume";
      }
    }

    return buttonText;
  };

  const totalPrice = getTotalPrice(purchase, purchasePreview);

  const isOnAftermarketDetailsStep =
    router.pathname === aftermarketDetailsStep.href;

  let rrLogoLink = "/purchase/coverage";

  if (purchase?.dealSource) {
    rrLogoLink += `?source=${purchase.dealSource}`;
  } else if (source) {
    rrLogoLink += `?source=${source}`;
  }

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const linkLocale = getAlternateLocaleByCode(router.locale || "en");
  const useUpdateUserLanguage = useUpdateUserLanguageMutation();

  const handleLocaleChange = () => {
    router.push(router.asPath, router.asPath, { locale: linkLocale.code });
    useUpdateUserLanguage.mutate(linkLocale.code);
  };

  const logoutMutation = useLogoutUserMutation();

  const onLogoutClick = () => {
    logoutMutation.mutate(undefined);
  };

  return (
    <CoreLayout>
      <LoginFlowWrapper
        isLoginModalOpen={showLoginModal}
        onLoginModalClose={onLoginModalClose}
        onLogin={onLogin}
        onEmailConfirmed={onLogin}
        loginModalMode={loginMode}
        socialLoginParams="loginSuccessful=true"
      >
        <div className="bg-white">
          <nav
            className={classNames(
              "relative flex flex-wrap items-center gap-x-1 lg:gap-x-2 py-2 px-2 lg:py-4 lg:px-8 border-b border-b-gray-50 justify-start lg:justify-between",
              {
                "2sm:flex-nowrap": isOnLanding,
                "xl:flex-nowrap": !isOnLanding,
              }
            )}
          >
            <Link
              href={rrLogoLink}
              passHref
              role="link"
              tabIndex={0}
              className="flex-shrink-0"
            >
              <LocalImage
                width="173"
                height="39"
                className="hover:cursor-pointer h-auto w-28 sm:w-32 lg:w-[173px] mb-2 lg:mb-0"
                src={RightRideLogo}
                alt="RightRide Logo"
                priority
              />
            </Link>

            {isOnLanding && (
              <div className="flex flex-wrap items-center sm:items-end lg:mb-[8px] space-x-1 w-full sm:w-auto">
                <Trans
                  t={t}
                  i18nKey="common:in_association_with_and"
                  components={{
                    1: (
                      <span className="text-[10px] font-semibold uppercase text-blueZodiac" />
                    ),
                    2: (
                      <LocalImage
                        className="h-auto w-8 lg:w-[60px] mb-[1px] lg:mb-[-5px]"
                        src={KijijiLogo}
                        alt="Kijiji Logo"
                        priority
                      />
                    ),
                    3: (
                      <span className="text-[10px] font-semibold uppercase text-blueZodiac" />
                    ),
                    4: (
                      <LocalImage
                        className="h-auto w-8 lg:w-[60px] mb-[3px]"
                        src={IALogo}
                        alt="IA Logo"
                        priority
                      />
                    ),
                  }}
                />
              </div>
            )}

            {currentStep && !currentStep.isPointOfNoReturn && (
              <PurchaseStepper
                steps={getAllPurchaseStepsForFlow("aftermarket")}
                currentStep={currentStep}
                previousStep={previousStep}
              />
            )}

            {isOnLanding && (
              <div className="absolute top-0 right-0">
                {user && user.isLoggedIn && (
                  <Button
                    fill="link"
                    spacing="tight-hug"
                    size="small"
                    className="px-[.1rem] lg:hidden"
                    onClick={() => {
                      router.push("/profile");
                    }}
                  >
                    <CustomerAvatar
                      customer={user}
                      size="sm"
                      isCurrentUser
                      isProfileAvatar
                    />
                  </Button>
                )}
                <HamburgerMenu
                  onMenuIconClick={setIsMenuOpen}
                  isMenuOpen={isMenuOpen}
                  menuColor="text-persianBlue"
                />
              </div>
            )}

            <div
              className={`ml-auto mr-4 xl:order-3 ${
                isOnLanding ? "hidden lg:block" : "block"
              }`}
            >
              <LocaleButton />
            </div>
            <div
              className={`order-2 xl:order-3 ${
                isOnLanding ? "hidden lg:block" : "block"
              }`}
            >
              <CollapsibleLoginButton
                user={user}
                buttonText={getLoginButtonText()}
                onLoginClick={onLoginClick}
                onProfileAvatarClick={() =>
                  router.push("/profile", "/profile", { locale: "en" })
                }
              />
            </div>
          </nav>

          {isOnLanding && isMenuOpen && (
            <div
              className={classNames(
                "bg-gray-25 p-4 sticky top-0 z-50 lg:hidden",
                {
                  hidden: currentStep?.isPointOfNoReturn,
                }
              )}
            >
              <Transition
                className="lg:hidden"
                show={isMenuOpen}
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
              >
                {(!user || !user.isLoggedIn) && (
                  <Button
                    fill="outline"
                    buttonStyle="transparent"
                    border="none"
                    leftIcon={<AccountIcon className="h-6 w-6" />}
                    className="w-full text-left text-primary-deep"
                    childWidth="full"
                    onClick={() => onLoginClick()}
                    onKeyDown={() => onLoginClick()}
                  >
                    <span className="pl-1">
                      {t(getLoginButtonText() as TranslationKeys["common"])}
                    </span>
                  </Button>
                )}
                {user && user.isLoggedIn && (
                  <Button
                    fill="outline"
                    buttonStyle="transparent"
                    border="none"
                    leftIcon={<LogoutIcon className="h-6 w-6" />}
                    className="w-full text-left text-primary-deep"
                    childWidth="full"
                    onClick={() => onLogoutClick()}
                    onKeyDown={() => onLogoutClick()}
                  >
                    <span className="pl-1">{t("common:logout")}</span>
                  </Button>
                )}

                <Button
                  fill="outline"
                  buttonStyle="transparent"
                  border="none"
                  leftIcon={<LoadingIconGlobeIcon className="h-6 w-6" />}
                  className="w-full text-left text-primary-deep"
                  childWidth="full"
                  onClick={() => handleLocaleChange()}
                  onKeyDown={() => handleLocaleChange()}
                >
                  <span className="pl-1">
                    {t("common:switch_to_language", {
                      language:
                        locale === "en"
                          ? t("common:language_french")
                          : t("common:language_english"),
                    })}
                  </span>
                </Button>
              </Transition>
            </div>
          )}

          <ConsumerPurchaseLayoutContext.Provider
            value={consumerPurchaseLayoutContextState}
          >
            {!isOnLanding && purchase?.vehicle && (
              <div
                className={classNames(
                  "bg-gray-25 p-4 sticky top-0 z-50 lg:hidden",
                  {
                    hidden: currentStep?.isPointOfNoReturn,
                  }
                )}
              >
                {!currentStep?.isPointOfNoReturn && (
                  <div className="flex items-center justify-between">
                    <div
                      role="presentation"
                      className="flex-1 body-3 leading-6 hover:cursor-pointer"
                      onClick={() => setIsOrderSummaryOpen((prev) => !prev)}
                    >
                      <div className="flex gap-3 sm:gap-6">
                        {purchase.vehicle.image && (
                          <PurchaseSourceVehicleImage
                            className="rounded-xl w-1/3 md:w-1/4 max-h-24 object-cover"
                            src={purchase.vehicle.image}
                            alt="Vehicle picture for purchase"
                          />
                        )}

                        {purchase.vehicle &&
                          purchase.vehicle.vin &&
                          purchase.vehicle.year &&
                          purchase.vehicle.make &&
                          purchase.vehicle.model && (
                            <div className="flex flex-row gap-2 sm:gap-6 items-center w-full">
                              <div className="w-fit heading-3">
                                <p className="font-semibold">
                                  {purchase.vehicle.year}&nbsp;
                                  {purchase.vehicle.make}&nbsp;
                                  {purchase.vehicle.model}
                                </p>

                                {purchase.vehicle.trim && (
                                  <span className="mb-3 text-gray-500">
                                    {purchase.vehicle.trim}
                                  </span>
                                )}
                                {purchase.vehicle.odometerCount > 0 && (
                                  <span className="text-gray-500">
                                    {purchase.vehicle.trim ? " | " : ""}
                                    {purchase.vehicle.odometerCount.toLocaleString()}{" "}
                                    {t("common:km")}
                                  </span>
                                )}

                                {!isOnAftermarketDetailsStep &&
                                  totalPrice > 0 && (
                                    <p>
                                      {formatCurrency(
                                        totalPrice,
                                        locale || "en"
                                      )}
                                    </p>
                                  )}
                              </div>
                            </div>
                          )}
                        <div className="flex items-center">
                          {!isOrderSummaryOpen && (
                            <ChevronDownIcon className="inline ml-2" />
                          )}
                          {isOrderSummaryOpen && (
                            <ChevronUpIcon className="inline ml-2" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <Transition
                  show={isOrderSummaryOpen}
                  enter="transition duration-100 ease-out"
                  enterFrom="transform scale-95 opacity-0"
                  enterTo="transform scale-100 opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform scale-100 opacity-100"
                  leaveTo="transform scale-95 opacity-0"
                >
                  <div className="flex flex-col pt-4 bg-gray-25 justify-center">
                    <PurchaseInformation
                      purchasePreview={purchasePreview}
                      purchase={purchase}
                    />

                    <Button
                      fill="link"
                      leftIcon={<ArrowUpIcon className="h-4" />}
                      onClick={() => setIsOrderSummaryOpen(false)}
                    >
                      {t("common:show_less")}
                    </Button>
                  </div>
                </Transition>
              </div>
            )}
            <ChildrenContainer
              currentStep={currentStep}
              purchase={purchase}
              purchasePreview={purchasePreview}
              removePadding={removePadding}
              isOnIndex={isOnIndex}
            >
              {headerContainer && headerContainer}
              {previousStep && (
                <div className="hidden lg:flex mx-6 mb-6 ml-2 items-center">
                  <Link
                    className="flex gap-x-2 body-3 text-primary-bold"
                    href={{
                      pathname: previousStep.href,
                    }}
                    onClick={handleBackClick}
                  >
                    <ArrowLeftIcon className="w-5 h-5" />
                    <span>
                      {t("common:back")}{" "}
                      {previousStep
                        ? `${t("common:to")} ${t(previousStep.nameTranslation)}`
                        : ""}
                    </span>
                  </Link>
                </div>
              )}
              {children}
            </ChildrenContainer>
          </ConsumerPurchaseLayoutContext.Provider>
        </div>
      </LoginFlowWrapper>
      {purchase && (
        <CoverageOverwriteModal
          purchase={purchase}
          show={showOverwriteModal}
          onClose={() => setShowOverwriteModal(false)}
          onResumePurchaseClick={handleOnResumePurchaseClick}
          onOverwritePurchaseClick={handleOnOverwritePurchaseClick}
          disableClosing
        />
      )}

      <Modal
        isOpen={showModal}
        alignment="center"
        onClose={() => {
          setShowModal(false);
        }}
        title={t("purchase:update_contracts")}
      >
        <h5 className="text-primary-deep mb-3">
          {t("purchase:do_you_want_to_update_contracts")}
        </h5>
        <p>{t("purchase:if_change_sign_again")}</p>
        <Modal.Buttons
          primaryButtonText={t("purchase:i_want_update_contract")}
          onPrimaryClick={() => {
            if (previousStep) {
              router.push({
                pathname: previousStep.href,
              });
            }
          }}
          secondaryButtonText={t("common:cancel")}
          secondaryButtonFill="link"
          onSecondaryClick={() => {
            setShowModal(false);
          }}
          stretchButtonToContent
        />
      </Modal>

      {showCoverageDetailsModal && purchase && (
        <CoverageDetailsModal
          purchase={purchase}
          isOpen={showCoverageDetailsModal}
          onClose={() => setShowCoverageDetailsModal(false)}
        />
      )}

      {showPromoCodeApplied && (
        <PromoCodeAppliedModal
          promoCode={promoCodeName}
          isOpen={showPromoCodeApplied}
          onClose={() => {
            setShowPromoCodeApplied(false);
            refetchPurchase();
          }}
        />
      )}

      <LoadingSpinner isLoading={isPageLoading} isOverlay />
    </CoreLayout>
  );
};

export default CoveragePurchaseLayout;
